import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardList, AlertTriangle, Euro, ChevronRight, ChevronDown,
  Phone, Mail, MessageCircle, UserCheck, Clock, FileWarning,
  Loader2, FolderOpen, IdCard, Users, CalendarClock, CircleDot, CheckCircle2
} from "lucide-react";
import { currency, formatDate } from "@/utils/helpers";
// (MyWorkPanel ora montato in App.js sopra al main TabsList — 22/02/2026)
import api from "@/api";
import { canViewFinancialDashboard } from "@/lib/permissions";

const fmt = (v) => (v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("it-IT"); } catch { return d; }
};

const CONTACT_ICONS = { Telefonata: Phone, Email: Mail, WhatsApp: MessageCircle, Presenza: UserCheck };

// ─── Fluent UI sub-components (Dashboard Phase 2 — 01/07/2026) ─────
// Componenti visivi coerenti con Microsoft 365 (Outlook/Planner/Teams):
// bordi neutri, tipografia sobria, colori usati SOLO su indicatori di
// urgenza. Estratti a livello di modulo per evitare la creazione di
// nuovi component types ad ogni render del parent DashboardTab
// (React/eslint no-unstable-nested-components).
const ACCENT_BG = {
  red: "bg-red-500",
  orange: "bg-orange-500",
  yellow: "bg-yellow-500",
  green: "bg-emerald-500",
  blue: "bg-blue-500",
  slate: "bg-slate-400",
  // Brand v1.5.0 — palette Gruppo Impresa Suite
  brand: "gis-bg-green",
  gold: "bg-[var(--gis-gold)]",
};
const FluentPane = ({ title, count, subLabel, accent = "blue", children, onOpenAll, openLabel, testId }) => (
  <div className="bg-white border border-slate-200 rounded flex flex-col shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
      <span className={`h-1.5 w-1.5 rounded-sm ${ACCENT_BG[accent] || "bg-blue-500"}`} />
      <span className="gis-heading text-[11px] font-semibold uppercase tracking-wider text-slate-700 flex-1 truncate">{title}</span>
      {typeof count === "number" && (
        <span className="text-[11px] text-slate-500 tabular-nums shrink-0 gis-body">
          ({count}{subLabel ? ` · ${subLabel}` : ""})
        </span>
      )}
    </div>
    <div className="p-1 flex-1">{children}</div>
    {onOpenAll && (
      <button
        type="button"
        onClick={onOpenAll}
        className="gis-body w-full text-left px-3 py-1.5 border-t border-slate-100 text-[11px] font-medium hover:bg-[var(--gis-green-50)] transition-colors flex items-center gap-1"
        style={{ color: "var(--gis-green)" }}
      >
        {openLabel} <ChevronRight className="h-3 w-3" />
      </button>
    )}
  </div>
);
const FluentRow = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors text-left"
  >
    {children}
  </button>
);
const EmptyState = ({ icon: Ic, text }) => (
  <div className="flex flex-col items-center justify-center py-6 text-slate-300">
    <Ic className="h-5 w-5 mb-1" />
    <p className="text-[12px] text-slate-400">{text}</p>
  </div>
);
const MiniKpi = ({ label, value, sub, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="bg-white border border-slate-200 rounded px-3 py-2 text-left hover:border-[var(--gis-green)] hover:shadow-md transition-all flex flex-col justify-center min-h-[64px] group"
  >
    <p className="gis-heading text-[10px] uppercase tracking-wider text-slate-500 truncate group-hover:text-[var(--gis-green)] transition-colors">{label}</p>
    <p className="gis-heading text-base font-semibold text-slate-900 tabular-nums truncate">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 truncate gis-body">{sub}</p>}
  </button>
);

function DashboardTab({
  clients,
  cases,
  invoices,
  deadlines,
  activeCases,
  criticalDeadlines,
  upcomingDeadlines,
  unpaidInvoices,
  casesToBill,
  totalToBill,
  totalUnpaid,
  setActiveTab,
  navigateToCasesWithStatus,
  toggleDeadlineStatus,
  updateInvoiceStatus,
  getDeadlineUrgency,
  operators = [],
  currentOperator = null,
  operatorRole = null,
  documentExpiryAlerts = [],
  navigateToScadenzarioClienti
}) {
  // ─── Financial visibility (07/07/2026 P0 extension) ─────────────
  // La dashboard mostra i pannelli finanziari SOLO ai ruoli con
  // `canViewFinancialDashboard`. Operatore Studio e Agenzia vedono
  // il fallback "Pratiche ferme" al posto di "Fatture da incassare"
  // e non vedono i KPI economici (Da fatturare / Residuo incasso /
  // Fatture scadute / Solleciti). Vecchio flag `isStudioOperator`
  // rimosso per non discriminare ambiguamente tra Studio e Agenzia.
  const canSeeFinancial = canViewFinancialDashboard(operatorRole);

  // Visibilità widget "Completate oggi" — solo amministratori e responsabili.
  // Mirror ADMIN_ROLES nel backend (server.py).
  const isManagerRole = operatorRole === "amministratore" || operatorRole === "responsabile";

  const [recentContacts, setRecentContacts] = useState([]);
  // Lightweight cache delle registrazioni Diario completate OGGI. Caricato
  // dallo stesso fetch di `getContacts()` (no extra round-trip). Filter
  // applicato lato client: backend non offre ancora un endpoint
  // dedicato e dataset corrente (~1859 record) gestibile.
  const [completedTodayRows, setCompletedTodayRows] = useState([]);
  const [solleciti, setSolleciti] = useState(null);
  const [customDeadlines, setCustomDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Local UI: gruppi espansi nel widget "Completate oggi".
  const [expandedCompletedOp, setExpandedCompletedOp] = useState(null);

  // Load diary contacts, solleciti, and custom deadlines
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [contacts, sollData, deadlinesData] = await Promise.all([
          api.getContacts().catch(() => []),
          canSeeFinancial
            ? api.getSolleciti(String(new Date().getFullYear()), "agenzia").catch(() => [])
            : Promise.resolve(null),
          api.getCustomDeadlines().catch(() => []),
        ]);
        setRecentContacts(contacts.slice(0, 5));
        // Pre-filter completate-oggi qui per evitare di ri-iterare l'intero
        // array in fase di render (1859+ record). Se il widget non è
        // visibile per il ruolo corrente saltiamo proprio il filtro.
        if (isManagerRole) {
          const todayIso = new Date().toISOString().slice(0, 10);
          setCompletedTodayRows(
            contacts.filter((c) => (c.completedAt || "") === todayIso)
          );
        }
        setSolleciti(sollData);
        setCustomDeadlines(deadlinesData);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [canSeeFinancial, isManagerRole]);

  // Group "Completate oggi" by operator for the collapsible widget.
  // - chiavi raggruppate per `completedByOperatorId` (fallback al nome).
  // - ordinato per nome operatore.
  // - ogni gruppo conserva la lista delle righe ordinate per timestamp.
  const completedTodayGroups = useMemo(() => {
    if (!isManagerRole || completedTodayRows.length === 0) return [];
    const map = new Map();
    for (const c of completedTodayRows) {
      const key = c.completedByOperatorId || c.completedByOperatorName || "—";
      const bucket = map.get(key) || {
        key,
        operatorName: c.completedByOperatorName || "Operatore",
        rows: [],
      };
      bucket.rows.push(c);
      map.set(key, bucket);
    }
    // ordino per orario di completamento più recente (deriva dalla history
    // ultimo entry con action == diary_completed quando disponibile, altrimenti
    // fallback all'updatedAt del record).
    for (const g of map.values()) {
      g.rows.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
    }
    return [...map.values()].sort((a, b) =>
      (a.operatorName || "").localeCompare(b.operatorName || "")
    );
  }, [completedTodayRows, isManagerRole]);

  // Helper: estrai l'orario "HH:MM" dell'evento `diary_completed` dalla
  // history, se presente; altrimenti fallback all'updatedAt.
  const getCompletedTimeLabel = (row) => {
    const ev = (row.history || []).slice().reverse().find(
      (h) => h.action === "diary_completed"
    );
    const ts = ev?.timestamp || row.updatedAt;
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  // Current month deadlines (active only, sorted by date)
  const monthDeadlines = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return customDeadlines
      .filter(d => {
        if (d.status === "completata") return false;
        const dd = new Date(d.deadlineDate);
        return dd.getMonth() === currentMonth && dd.getFullYear() === currentYear;
      })
      .sort((a, b) => a.deadlineDate.localeCompare(b.deadlineDate));
  }, [customDeadlines]);

  // Urgency color for deadline
  const getDeadlineColor = (dateStr) => {
    const now = new Date();
    const target = new Date(dateStr);
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { dot: "bg-red-500", text: "text-red-600", label: "Scaduta" };
    if (diff <= 3) return { dot: "bg-red-400", text: "text-red-500", label: `${diff}gg` };
    if (diff <= 7) return { dot: "bg-yellow-500", text: "text-yellow-600", label: `${diff}gg` };
    return { dot: "bg-green-500", text: "text-green-600", label: `${diff}gg` };
  };

  // Stale practices (no status change in 15+ days)
  const stalePractices = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 15);
    return (activeCases || []).filter(c => {
      const od = c.openingDate || c.createdAt || "";
      if (!od) return false;
      return new Date(od) < cutoff;
    }).slice(0, 5);
  }, [activeCases]);

  // Overdue invoices from solleciti
  const overdueInvoices = useMemo(() => {
    if (!solleciti?.invoices) return [];
    return solleciti.invoices.filter(i => i.giorniRitardo > 0);
  }, [solleciti]);

  const criticalInvoices = useMemo(() =>
    overdueInvoices.filter(i => i.giorniRitardo > 30),
  [overdueInvoices]);

  // Residuo totale
  const totalResiduo = useMemo(() =>
    invoices.reduce((s, inv) => s + ((inv.totale || 0) - (inv.totalPaid || 0)), 0),
  [invoices]);

  // ─── Fluent UI sub-components ora esportati a livello di modulo ────
  // (spostati sopra `function DashboardTab` per compliance con
  //  react/no-unstable-nested-components).

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="dashboard-tab">

      {/* ═════ RIGA 1 — COMANDO OPERATIVO PRIMARIO ═════
          Sinistra: Scadenze operative del mese (customDeadlines attive).
          Destra:   Documenti clienti / adempimenti antiriciclaggio
                    (documentExpiryAlerts). Elevato da KPI a pannello
                    primario su richiesta utente (01/07/2026).
          ═════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <FluentPane
          title="Scadenze del mese"
          count={monthDeadlines.length}
          accent="orange"
          onOpenAll={navigateToScadenzarioClienti}
          openLabel="Apri Scadenzario"
          testId="dash-pane-scadenze"
        >
          {monthDeadlines.length === 0 ? (
            <EmptyState icon={CalendarClock} text="Nessuna scadenza questo mese" />
          ) : (
            <>
              {monthDeadlines.slice(0, 5).map((d, i) => {
                const dc = getDeadlineColor(d.deadlineDate);
                return (
                  <FluentRow key={d.id || i} onClick={navigateToScadenzarioClienti} testId={`dash-month-deadline-${i}`}>
                    <span className={`h-2 w-2 rounded-full shrink-0 ${dc.dot}`} />
                    <span className="text-[13px] text-slate-500 tabular-nums w-20 shrink-0">{fmtDate(d.deadlineDate)}</span>
                    <span className="text-[13px] text-slate-800 truncate flex-1">{d.clientName}</span>
                    <span className={`text-[11px] font-medium shrink-0 ${dc.text}`}>{dc.label}</span>
                  </FluentRow>
                );
              })}
              {monthDeadlines.length > 5 && (
                <p className="text-[11px] text-slate-500 text-center pt-1">+ altre {monthDeadlines.length - 5} scadenze</p>
              )}
            </>
          )}
        </FluentPane>

        <FluentPane
          title="Documenti clienti - Adempimenti"
          count={documentExpiryAlerts.length}
          accent="red"
          onOpenAll={() => setActiveTab("clients")}
          openLabel="Gestisci documenti"
          testId="dash-pane-documenti"
        >
          {documentExpiryAlerts.length === 0 ? (
            <EmptyState icon={IdCard} text="Nessun documento in scadenza" />
          ) : (
            <>
              {documentExpiryAlerts.slice(0, 5).map((a, i) => {
                const isExpired = a.status === "expired" || a.daysUntil < 0;
                const color = isExpired
                  ? { dot: "bg-red-500", text: "text-red-600" }
                  : a.daysUntil <= 15
                    ? { dot: "bg-orange-500", text: "text-orange-600" }
                    : { dot: "bg-emerald-500", text: "text-emerald-600" };
                const statusLabel = isExpired
                  ? `Scaduto ${Math.abs(a.daysUntil)}g fa`
                  : `Scad. ${a.daysUntil}gg`;
                return (
                  <FluentRow
                    key={`${a.type}-${a.id}-${i}`}
                    onClick={() => setActiveTab(a.type === "cliente" ? "clients" : "impostazioni")}
                    testId={`dash-doc-expiry-${i}`}
                  >
                    <span className={`h-2 w-2 rounded-full shrink-0 ${color.dot}`} />
                    <span className="text-[13px] text-slate-800 truncate flex-1 min-w-0">{a.name}</span>
                    <span className="text-[11px] text-slate-500 truncate w-28 shrink-0">{a.documentType}</span>
                    <span className="text-[13px] text-slate-500 tabular-nums w-20 shrink-0">{a.expiryDate}</span>
                    <span className={`text-[11px] font-medium shrink-0 ${color.text}`}>{statusLabel}</span>
                  </FluentRow>
                );
              })}
              {documentExpiryAlerts.length > 5 && (
                <p className="text-[11px] text-slate-500 text-center pt-1">+ altri {documentExpiryAlerts.length - 5} documenti</p>
              )}
            </>
          )}
        </FluentPane>
      </div>

      {/* ═════ RIGA 2 — FOLLOW-UP OPERATIVO ═════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <FluentPane
          title="Clienti da ricontattare"
          count={recentContacts.length}
          accent="brand"
          onOpenAll={() => setActiveTab("rubrica")}
          openLabel="Apri Diario"
          testId="dash-pane-ricontatti"
        >
          {recentContacts.length === 0 ? (
            <EmptyState icon={MessageCircle} text="Nessuna attività recente" />
          ) : (
            recentContacts.slice(0, 5).map((c, i) => {
              const Ic = CONTACT_ICONS[c.contactType] || Phone;
              return (
                <FluentRow key={c.id || i} onClick={() => setActiveTab("rubrica")} testId={`dash-recontatto-${i}`}>
                  <Ic className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="text-[13px] text-slate-800 truncate w-32 shrink-0">{c.clientName}</span>
                  <span className="text-[11px] text-slate-500 truncate w-20 shrink-0">{c.contactType}</span>
                  <span className="text-[13px] text-slate-500 truncate flex-1">{(c.content || "").slice(0, 60)}</span>
                  <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{fmtDate(c.contactDate)}</span>
                </FluentRow>
              );
            })
          )}
        </FluentPane>

        {canSeeFinancial ? (
          <FluentPane
            title="Fatture da incassare"
            count={overdueInvoices.length}
            subLabel={overdueInvoices.length > 0 ? `${fmt(overdueInvoices.reduce((s, i) => s + (i.residuo || 0), 0))} €` : null}
            accent="red"
            onOpenAll={() => setActiveTab("fatturazione")}
            openLabel="Vai a Fatturazione"
            testId="dash-pane-fatture-scadute"
          >
            {overdueInvoices.length === 0 ? (
              <EmptyState icon={CheckCircle2} text="Nessuna fattura scaduta" />
            ) : (
              overdueInvoices.slice(0, 5).map((inv, i) => {
                const days = inv.giorniRitardo || 0;
                const color = days > 30
                  ? { dot: "bg-red-500", text: "text-red-600" }
                  : days > 15
                    ? { dot: "bg-orange-500", text: "text-orange-600" }
                    : { dot: "bg-yellow-500", text: "text-yellow-600" };
                return (
                  <FluentRow key={inv.id || i} onClick={() => setActiveTab("fatturazione")} testId={`dash-overdue-${i}`}>
                    <span className={`h-2 w-2 rounded-full shrink-0 ${color.dot}`} />
                    <span className="font-mono text-[11px] text-slate-500 w-14 shrink-0 truncate">{inv.invoiceNumber}</span>
                    <span className="text-[13px] text-slate-800 truncate flex-1">{inv.clientName}</span>
                    <span className="text-[13px] text-slate-700 tabular-nums shrink-0">{fmt(inv.residuo)} &euro;</span>
                    <span className={`text-[11px] font-medium shrink-0 ${color.text}`}>{days}gg</span>
                  </FluentRow>
                );
              })
            )}
          </FluentPane>
        ) : (
          <FluentPane
            title="Pratiche ferme"
            count={stalePractices.length}
            accent="slate"
            onOpenAll={() => setActiveTab("cases")}
            openLabel="Apri Registro"
            testId="dash-pane-stale"
          >
            {stalePractices.length === 0 ? (
              <EmptyState icon={FolderOpen} text="Nessuna pratica ferma" />
            ) : (
              stalePractices.slice(0, 5).map((c, i) => (
                <FluentRow key={c.id || i} onClick={() => setActiveTab("cases")} testId={`dash-stale-${i}`}>
                  <span className="h-2 w-2 rounded-full shrink-0 bg-slate-400" />
                  <span className="text-[13px] text-slate-800 truncate flex-1">{c.clientName}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">Ferma da 15+gg</span>
                </FluentRow>
              ))
            )}
          </FluentPane>
        )}
      </div>

      {/* ═════ RIGA 3 — KPI ECONOMICI COMPATTI ═════
          Strip di tile 64 px altezza, cliccabili, con numero senza
          esagerazioni ("text-base" non "text-3xl"). Visibilità KPI
          fatturazione limitata ai ruoli non-studio (invariata rispetto
          alla versione precedente). ═════════════════════════════════ */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${!canSeeFinancial ? 'lg:grid-cols-2' : 'xl:grid-cols-5'} gap-3`}>
        <MiniKpi
          label="Pratiche attive"
          value={activeCases?.length || 0}
          sub={`${(cases || []).filter(c => c.practiceType === "Gestionale Agenzia" && !["Chiusa","Conclusa"].includes(c.status)).length}A · ${(cases || []).filter(c => c.practiceType === "Gestionale Studio" && !["Chiusa","Conclusa"].includes(c.status)).length}S`}
          onClick={() => setActiveTab("cases")}
          testId="dash-kpi-pratiche-attive"
        />
        {canSeeFinancial && (
          <>
            <MiniKpi
              label="Da fatturare"
              value={casesToBill?.length || 0}
              sub={totalToBill > 0 ? `${fmt(totalToBill)} €` : "—"}
              onClick={() => setActiveTab("fatturazione")}
              testId="dash-kpi-da-fatturare"
            />
            <MiniKpi
              label="Residuo incasso"
              value={`${fmt(totalResiduo)} €`}
              sub={`${unpaidInvoices?.length || 0} fatture`}
              onClick={() => setActiveTab("report")}
              testId="dash-kpi-residuo"
            />
            <MiniKpi
              label="Fatture scadute"
              value={overdueInvoices.length}
              sub={criticalInvoices.length > 0 ? `${criticalInvoices.length} > 30gg` : "—"}
              onClick={() => setActiveTab("fatturazione")}
              testId="dash-kpi-fatture-scadute"
            />
            <MiniKpi
              label="Solleciti"
              value={overdueInvoices.filter(i => i.livello !== "nessuno").length}
              sub={overdueInvoices.length > 0 ? `${fmt(overdueInvoices.reduce((s,i)=>s+(i.residuo||0),0))} €` : "—"}
              onClick={() => setActiveTab("fatturazione")}
              testId="dash-kpi-solleciti"
            />
          </>
        )}
        {!canSeeFinancial && (
          <MiniKpi
            label="Pratiche ferme"
            value={stalePractices.length}
            sub="da >15gg"
            onClick={() => setActiveTab("cases")}
            testId="dash-kpi-pratiche-ferme"
          />
        )}
      </div>

      {/* ═════ RIGA 4 — COMPLETATE OGGI (admin/responsabile · single-row) ═════
          Widget lightweight collassato di default. Il click sull'header
          espande la lista raggruppata per operatore (invariato).
          ══════════════════════════════════════════════════════════════════ */}
      {isManagerRole && (
        <div
          className="rounded border border-slate-200 bg-white overflow-hidden"
          data-testid="dash-completate-oggi-widget"
        >
          <button
            type="button"
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
            onClick={() => setExpandedCompletedOp(expandedCompletedOp === "__ALL__" ? null : "__ALL__")}
            data-testid="dash-completate-toggle-all"
          >
            {expandedCompletedOp === "__ALL__"
              ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 shrink-0">Completate oggi</span>
            <span className="text-[13px] font-medium text-slate-800 tabular-nums shrink-0">{completedTodayRows.length}</span>
            <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
              {completedTodayGroups.map(g => (
                <span key={g.key} className="text-[11px] text-slate-500 truncate">
                  <span className="text-slate-700">{g.operatorName}</span>{" "}
                  <span className="tabular-nums">{g.rows.length}</span>
                </span>
              ))}
            </div>
            <span className="text-[11px] text-blue-600 shrink-0">
              {expandedCompletedOp === "__ALL__" ? "Chiudi" : "Apri"}
            </span>
          </button>
          {expandedCompletedOp === "__ALL__" && (
            <div className="border-t border-slate-100 px-3 py-2 space-y-2 max-h-[300px] overflow-y-auto">
              {completedTodayGroups.length === 0 ? (
                <div className="text-[13px] text-slate-400 text-center py-3">
                  Nessuna registrazione del Diario completata oggi
                </div>
              ) : (
                completedTodayGroups.map(g => (
                  <div key={g.key} data-testid={`dash-completate-group-${g.key}`}>
                    <div className="flex items-center gap-2 py-1">
                      <UserCheck className="h-3 w-3 text-emerald-600" />
                      <span className="text-[13px] font-medium text-slate-800">{g.operatorName}</span>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{g.rows.length}</Badge>
                    </div>
                    <div className="pl-5 space-y-0.5">
                      {g.rows.map(row => {
                        const time = getCompletedTimeLabel(row);
                        return (
                          <div
                            key={row.id}
                            className="flex items-center gap-2 text-[12px] text-slate-600 py-0.5 hover:bg-slate-50 cursor-pointer rounded px-1"
                            onClick={() => setActiveTab("attivita")}
                            data-testid={`dash-completate-row-${row.id}`}
                          >
                            <Clock className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="font-mono text-[11px] text-slate-500 w-10 shrink-0">{time || "--:--"}</span>
                            <span className="font-medium text-slate-700 truncate w-32 shrink-0">{row.clientName || "—"}</span>
                            <span className="text-slate-500 truncate flex-1">{(row.content || "").slice(0, 80)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}


export default React.memo(DashboardTab);

